import { Link, createFileRoute } from "@tanstack/react-router";
import { MerchantExperienceBar } from "@/components/renata/MerchantExperienceBar";
import { useCallback, useEffect, useState } from "react";
import { ClinicalPageShell, ClinicalStatCard } from "@/components/clinical";
import { CrudEmptyState, CrudShellHeader, Loading } from "@/components/ds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { apiDelete, apiGetJson, apiSendJson } from "@/lib/sm-api";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { toast } from "sonner";
import { CalendarClock, Loader2, MoreVertical, Pencil, Plus, Power, Search, Trash2, UserCheck, Users, UserX } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ServiceProfessional } from "@/lib/db-service/service-professionals";
import type { ServiceCatalogItem } from "@/lib/db-service/service-catalog";
import { ProfessionalCalendarConnect } from "@/components/services/ProfessionalCalendarConnect";
import { syncProfessionalServiceLinks } from "@/lib/service-admin-links";
import { ImageUploadField } from "@/components/ImageUploadField";

export const Route = createFileRoute("/app/profesionales")({
  component: Page,
  head: () => ({ meta: [{ title: "Profesionales" }] }),
});

type FormState = {
  name: string;
  email: string;
  phone: string;
  bio: string;
  commissionPct: string;
  active: boolean;
  avatarUrl: string | null;
};

const EMPTY_FORM: FormState = {
  name: "",
  email: "",
  phone: "",
  bio: "",
  commissionPct: "0",
  active: true,
  avatarUrl: null,
};

function toForm(p: ServiceProfessional): FormState {
  return {
    name: p.name,
    email: p.email ?? "",
    phone: p.phone ?? "",
    bio: p.bio ?? "",
    commissionPct: String(p.commissionPct),
    active: p.active,
    avatarUrl: p.avatarUrl,
  };
}

function hashHue(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = id.charCodeAt(i) + ((h << 5) - h);
  return `hsl(${Math.abs(h) % 360} 48% 42%)`;
}

function ProfAvatar({ prof, size = 44 }: { prof: ServiceProfessional; size?: number }) {
  const initials = prof.name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  if (prof.avatarUrl) {
    return (
      <img
        src={prof.avatarUrl}
        alt=""
        className="shrink-0 rounded-full object-cover"
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <div
      className="flex shrink-0 items-center justify-center rounded-full text-xs font-bold text-white"
      style={{ width: size, height: size, background: hashHue(prof.id) }}
    >
      {initials}
    </div>
  );
}

type ScheduleRow = { professionalId: string; dayOfWeek: number; startTime: string; endTime: string };

const DAY_LABEL: Record<number, string> = { 0: "Dom", 1: "Lun", 2: "Mar", 3: "Mié", 4: "Jue", 5: "Vie", 6: "Sáb" };
const DAY_ORDER = [1, 2, 3, 4, 5, 6, 0];

function ScheduleSummary({ rows, professionalId }: { rows: ScheduleRow[]; professionalId: string }) {
  const mine = rows
    .filter((r) => r.professionalId === professionalId)
    .sort((a, b) => DAY_ORDER.indexOf(a.dayOfWeek) - DAY_ORDER.indexOf(b.dayOfWeek));
  if (mine.length === 0) {
    return <span className="text-xs text-muted-foreground">Sin horario configurado</span>;
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {mine.map((r) => (
        <Badge key={r.dayOfWeek} variant="secondary" className="text-[11px] font-normal">
          {DAY_LABEL[r.dayOfWeek]} {r.startTime}–{r.endTime}
        </Badge>
      ))}
    </div>
  );
}

function Page() {
  const tenantId = useWorkspaceTenantId();
  const [loading, setLoading] = useState(true);
  const [professionals, setProfessionals] = useState<ServiceProfessional[]>([]);
  const [services, setServices] = useState<ServiceCatalogItem[]>([]);
  const [schedules, setSchedules] = useState<ScheduleRow[]>([]);
  const [links, setLinks] = useState<Array<{ professionalId: string; serviceId: string }>>([]);
  const [editing, setEditing] = useState<ServiceProfessional | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [linkedServiceIds, setLinkedServiceIds] = useState<string[]>([]);
  const [initialLinkedServiceIds, setInitialLinkedServiceIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ServiceProfessional | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const [p, s, sc, l] = await Promise.all([
        apiGetJson<{ items: ServiceProfessional[] }>("/api/service-admin?resource=professionals", tenantId),
        apiGetJson<{ items: ServiceCatalogItem[] }>("/api/service-admin?resource=services", tenantId),
        apiGetJson<{ items: ScheduleRow[] }>("/api/service-admin?resource=schedules", tenantId),
        apiGetJson<{ items: Array<{ professionalId: string; serviceId: string }> }>(
          "/api/service-admin?resource=professional-services",
          tenantId,
        ),
      ]);
      setProfessionals(p.items ?? []);
      setServices(s.items ?? []);
      setSchedules(sc.items ?? []);
      setLinks(l.items ?? []);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "No se pudo cargar");
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  function openCreate() {
    setEditing(null);
    setForm(EMPTY_FORM);
    setLinkedServiceIds([]);
    setInitialLinkedServiceIds([]);
    setDialogOpen(true);
  }

  function openEdit(p: ServiceProfessional) {
    setEditing(p);
    setForm(toForm(p));
    const ids = links.filter((l) => l.professionalId === p.id).map((l) => l.serviceId);
    setLinkedServiceIds(ids);
    setInitialLinkedServiceIds(ids);
    setDialogOpen(true);
  }

  async function save() {
    if (!tenantId || !form.name.trim()) {
      toast.error("El nombre es obligatorio");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        bio: form.bio.trim() || null,
        commissionPct: Number(form.commissionPct || 0),
        active: form.active,
        avatarUrl: form.avatarUrl,
      };
      let professionalId = editing?.id;
      if (editing) {
        await apiSendJson(`/api/service-admin?resource=professionals&id=${editing.id}`, "PATCH", payload, tenantId);
      } else {
        const r = await apiSendJson<{ item: ServiceProfessional }>(
          "/api/service-admin?resource=professionals",
          "POST",
          payload,
          tenantId,
        );
        professionalId = r.item.id;
        await apiSendJson(`/api/service-admin?resource=professionals&id=${professionalId}`, "PATCH", payload, tenantId);
      }
      if (professionalId) {
        await syncProfessionalServiceLinks(tenantId, professionalId, linkedServiceIds, initialLinkedServiceIds);
      }
      toast.success(editing ? "Profesional actualizado" : "Profesional creado");
      setDialogOpen(false);
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(p: ServiceProfessional) {
    if (!tenantId) return;
    try {
      await apiSendJson(`/api/service-admin?resource=professionals&id=${p.id}`, "PATCH", { active: !p.active }, tenantId);
      toast.success(p.active ? "Desactivado" : "Activado");
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Error");
    }
  }

  function openDeleteDialog(p: ServiceProfessional) {
    setDeleteError(null);
    setDeleteTarget(p);
  }

  function closeDeleteDialog() {
    if (deleting) return;
    setDeleteTarget(null);
    setDeleteError(null);
  }

  async function confirmDelete() {
    if (!deleteTarget || !tenantId || deleting) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await apiDelete(`/api/service-admin?resource=professionals&id=${deleteTarget.id}`, tenantId);
      toast.success("Profesional eliminado");
      setDeleteTarget(null);
      await load();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "No se pudo eliminar el profesional";
      setDeleteError(msg);
      toast.error(msg);
    } finally {
      setDeleting(false);
    }
  }

  async function deactivateInstead() {
    if (!deleteTarget || !tenantId || deleting) return;
    setDeleting(true);
    try {
      await apiSendJson(`/api/service-admin?resource=professionals&id=${deleteTarget.id}`, "PATCH", { active: false }, tenantId);
      toast.success("Profesional desactivado");
      setDeleteTarget(null);
      setDeleteError(null);
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "No se pudo desactivar");
    } finally {
      setDeleting(false);
    }
  }

  const activeCount = professionals.filter((p) => p.active).length;
  const normalizedSearch = search.trim().toLowerCase();
  const visibleProfessionals = normalizedSearch
    ? professionals.filter(
        (p) =>
          p.name.toLowerCase().includes(normalizedSearch) ||
          (p.email ?? "").toLowerCase().includes(normalizedSearch),
      )
    : professionals;

  return (
    <ClinicalPageShell offset="none" className="pt-6">
      <MerchantExperienceBar screenId="profesionales" />
      <CrudShellHeader
        title="Profesionales"
        subtitle={`${activeCount} activos de ${professionals.length}`}
        count={professionals.length}
        actions={
          <Button size="sm" className="gap-2" onClick={openCreate}>
            <Plus className="h-4 w-4" /> Agregar profesional
          </Button>
        }
      />
      {loading ? (
        <Loading />
      ) : professionals.length === 0 ? (
        <CrudEmptyState title="Profesionales" description="Todavía no hay profesionales." />
      ) : (
        <div className="space-y-4 px-4 pb-8">
          <div className="grid gap-3 sm:grid-cols-3">
            <ClinicalStatCard label="Total" value={professionals.length} icon={Users} variant="primary" />
            <ClinicalStatCard label="Activos" value={activeCount} icon={UserCheck} variant="success" />
            <ClinicalStatCard label="Inactivos" value={professionals.length - activeCount} icon={UserX} />
          </div>
          <div className="relative max-w-sm">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar profesional…"
              className="pl-8"
              data-testid="professionals-search"
            />
          </div>
          {visibleProfessionals.length === 0 ? (
            <p className="text-sm text-muted-foreground">Ningún profesional coincide con "{search}".</p>
          ) : null}
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {visibleProfessionals.map((p) => {
              return (
                <div
                  key={p.id}
                  className="group flex flex-col rounded-xl border bg-card p-4 transition-colors hover:border-primary/30"
                >
                  <div className="flex items-center gap-3">
                    <ProfAvatar prof={p} size={48} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <h3 className="truncate text-sm font-semibold">{p.name}</h3>
                        <span
                          className={cn("h-2 w-2 shrink-0 rounded-full", p.active ? "bg-emerald-500" : "bg-muted-foreground/40")}
                          title={p.active ? "Activo" : "Inactivo"}
                        />
                      </div>
                      {p.email ? <p className="truncate text-xs text-muted-foreground">{p.email}</p> : null}
                      {p.phone ? <p className="truncate text-xs text-muted-foreground">{p.phone}</p> : null}
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(p)} title="Editar">
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreVertical className="h-3.5 w-3.5" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => void toggleActive(p)}>
                            <Power className="mr-2 h-3.5 w-3.5" />
                            {p.active ? "Desactivar" : "Activar"}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={() => openDeleteDialog(p)}
                          >
                            <Trash2 className="mr-2 h-3.5 w-3.5" />
                            Eliminar
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                  {p.bio ? <p className="mt-2 line-clamp-2 text-xs text-muted-foreground">{p.bio}</p> : null}
                  {p.commissionPct > 0 ? (
                    <p className="mt-2 text-xs text-muted-foreground">{p.commissionPct}% comisión</p>
                  ) : null}
                  {(() => {
                    const ids = links.filter((l) => l.professionalId === p.id).map((l) => l.serviceId);
                    const chips = services.filter((s) => ids.includes(s.id));
                    return chips.length > 0 ? (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {chips.map((s) => (
                          <Badge key={s.id} variant="secondary" className="text-[10px] font-normal">
                            {s.name}
                          </Badge>
                        ))}
                      </div>
                    ) : null;
                  })()}
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <ProfessionalCalendarConnect professionalId={p.id} />
                  </div>
                  <div className="mt-3 border-t pt-3">
                    <p className="mb-1.5 flex items-center justify-between gap-1 text-[11px] font-medium text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <CalendarClock className="h-3 w-3" /> Horario
                      </span>
                      <Button size="sm" variant="link" className="h-auto p-0 text-[11px]" asChild>
                        <Link to="/app/horarios" search={{ professionalId: p.id }}>
                          Editar
                        </Link>
                      </Button>
                    </p>
                    <ScheduleSummary rows={schedules} professionalId={p.id} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar profesional" : "Agregar profesional"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <ImageUploadField
              kind="avatar"
              value={form.avatarUrl}
              onChange={(url) => setForm((f) => ({ ...f, avatarUrl: url }))}
              tenantId={tenantId}
            />
            <div className="space-y-1.5">
              <Label>Nombre</Label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
              </div>
              <div className="space-y-1.5">
                <Label>Teléfono</Label>
                <Input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Bio</Label>
              <Textarea value={form.bio} onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-3 items-end">
              <div className="space-y-1.5">
                <Label>% comisión</Label>
                <Input type="number" value={form.commissionPct} onChange={(e) => setForm((f) => ({ ...f, commissionPct: e.target.value }))} />
              </div>
              <label className="flex items-center gap-2 pb-2 text-sm">
                <Switch checked={form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))} />
                Activo
              </label>
            </div>

            <div className="space-y-1.5 border-t pt-3">
              <Label>Servicios que presta</Label>
              <div className="grid grid-cols-2 gap-1.5">
                {services.map((s) => (
                  <label key={s.id} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={linkedServiceIds.includes(s.id)}
                      onCheckedChange={(checked) =>
                        setLinkedServiceIds((ids) => (checked ? [...ids, s.id] : ids.filter((id) => id !== s.id)))
                      }
                    />
                    {s.name}
                  </label>
                ))}
                {services.length === 0 && (
                  <p className="text-xs text-muted-foreground">No hay servicios cargados todavía.</p>
                )}
              </div>
            </div>

            <Button className="w-full" disabled={saving} onClick={() => void save()}>
              {saving ? "Guardando…" : editing ? "Guardar cambios" : "Agregar profesional"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteTarget != null} onOpenChange={(v) => !v && closeDeleteDialog()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar a {deleteTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>
                  Solo se puede eliminar un profesional sin turnos ni comisiones asociadas. Si tiene actividad
                  registrada, desactivalo para ocultarlo de nuevos turnos sin perder el historial.
                </p>
                {deleteError ? (
                  <p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-destructive">
                    {deleteError}
                  </p>
                ) : null}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col gap-2 sm:flex-row sm:justify-end">
            <AlertDialogCancel disabled={deleting}>Cancelar</AlertDialogCancel>
            {deleteError ? (
              <Button type="button" variant="secondary" disabled={deleting} onClick={() => void deactivateInstead()}>
                {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Desactivar en su lugar"}
              </Button>
            ) : null}
            <AlertDialogAction
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={(e) => {
                e.preventDefault();
                void confirmDelete();
              }}
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Eliminar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ClinicalPageShell>
  );
}
